/**
 * 常時効果システム: エフェクトエンジン
 *
 * このファイルは常時効果を管理・計算するメインエンジンを提供します。
 * カードの登録/解除、各種計算メソッドを実装しています。
 *
 * @see src/ルール/continuous-effect-system-design.md
 */

import { CONTINUOUS_EFFECT_TYPES } from './effectTypes';
import type { ContinuousEffectType } from './effectTypes';
import { TARGET_TYPES } from './targetTypes';
import type { TargetType } from './targetTypes';
import { checkCondition } from './conditionChecker';
import type { Condition, ConditionContext } from './conditionChecker';
import { calculateValue, setEffectUniqueId } from './valueCalculator';
import type { EffectDefinition } from './valueCalculator';
import type { MonsterInstance, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';

// ========================================
// 型定義
// ========================================

/** エンジン内部で使用する効果エントリ */
interface ActiveEffect extends EffectDefinition {
  type: ContinuousEffectType;
  target?: TargetType;
  condition?: Condition;
  targetCondition?: Condition;  // 攻撃対象条件（例: 飛行モンスターへの攻撃時）
  uniqueId: string;
  cardId: string;
  owner: PlayerNumber;
  usedThisTurn: boolean;
  stage?: number;
  // ダメージ関連
  damageSource?: 'battle' | 'effect' | 'all';
  usesPerTurn?: number;
  // 技無効関連
  skillType?: 'basic' | 'advanced' | 'all';
  // 召喚バフ関連
  statType?: 'atk' | 'attack' | 'hp';
  // SP制限関連
  restriction?: string;
}

/** カードとその効果を保持するエントリ */
interface EffectEntry {
  card: CardWithId | MonsterInstance;
  owner: PlayerNumber;
  effects: ActiveEffect[];
  stage?: number;
}

/** 効果計算用コンテキスト */
interface EffectContext extends ConditionContext {
  currentPlayer?: PlayerNumber;
  [key: string]: unknown;
}

/** 修正詳細の結果 */
interface ModifierDetails {
  modifier: number;
  sources: Array<{ name: string; value: number }>;
}

/** コスト修正詳細の結果 */
interface CostModifierDetails {
  modifier: number;
  sources: string[];
}

/** 召喚バフの結果 */
interface SummonBuffs {
  atkBuff: number;
  hpBuff: number;
}

/** SP制限情報 */
interface SPRestrictionInfo {
  restriction: string;
  cardId: string;
}

/** ターゲット判定用のカード型 */
interface TargetCard {
  uniqueId?: string;
  owner?: PlayerNumber;
  [key: string]: unknown;
}

/** 効果定義のレジストリ型 */
type EffectDefinitionsRegistry = Record<string, EffectDefinition[]>;

/** フェイズカード効果定義の型（段階別） */
type PhaseCardEffectsByStage = Record<string, Record<number, EffectDefinition[]>>;

// 効果定義は後で effectDefinitions/index.js からインポート
// 循環参照を避けるため、動的にインポートする方式を採用
let effectDefinitions: EffectDefinitionsRegistry = {};

// フェイズカード用の段階別効果定義
let phaseCardEffectsByStage: PhaseCardEffectsByStage = {};

/**
 * 効果定義を設定（外部から注入）
 * @param definitions - 効果定義オブジェクト
 */
export const setEffectDefinitions = (definitions: EffectDefinitionsRegistry): void => {
  effectDefinitions = definitions;
};

/**
 * フェイズカード効果定義を設定（外部から注入）
 * @param definitions - フェイズカード効果定義オブジェクト
 */
export const setPhaseCardEffectDefinitions = (definitions: PhaseCardEffectsByStage): void => {
  phaseCardEffectsByStage = definitions;
};

/**
 * 常時効果エンジンクラス
 */
class ContinuousEffectEngine {
  /**
   * アクティブな効果を管理するMap
   * uniqueId => { card, owner, effects[] }
   */
  private activeEffects: Map<string, EffectEntry>;

  constructor() {
    this.activeEffects = new Map();
  }

  // ========================================
  // ライフサイクル管理
  // ========================================

  /**
   * カードの常時効果を登録
   * @param card - カードオブジェクト
   * @param owner - オーナー（1 or 2）
   */
  register(card: CardWithId | MonsterInstance, owner: PlayerNumber): void {
    if (!card || !card.uniqueId) {
      console.warn('Cannot register card without uniqueId');
      return;
    }

    // 効果定義を取得
    const definitions = effectDefinitions[card.id];
    if (!definitions || definitions.length === 0) {
      // このカードには常時効果がない
      return;
    }

    // 効果にメタ情報を付与
    const effects: ActiveEffect[] = definitions.map((def) => ({
      ...def,
      type: def.type as ContinuousEffectType,
      uniqueId: card.uniqueId,
      cardId: card.id,
      owner,
      usedThisTurn: false,
    }));

    this.activeEffects.set(card.uniqueId, { card, owner, effects });
  }

  /**
   * カードの常時効果を解除
   * @param uniqueId - カードのuniqueId
   */
  unregister(uniqueId: string): void {
    this.activeEffects.delete(uniqueId);
  }

  /**
   * フェイズカードの常時効果を登録（段階指定）
   * @param card - フェイズカード
   * @param owner - オーナー（1 or 2）
   * @param stage - 現在の段階（0-3）
   */
  registerPhaseCard(card: CardWithId | MonsterInstance, owner: PlayerNumber, stage: number = 0): void {
    if (!card || !card.uniqueId) {
      console.warn('Cannot register phase card without uniqueId');
      return;
    }

    // フェイズカード用の効果定義を取得
    const cardStageEffects = phaseCardEffectsByStage[card.id];
    if (!cardStageEffects) {
      // このフェイズカードには常時効果がない
      return;
    }

    const stageEffects = cardStageEffects[stage];
    if (!stageEffects || stageEffects.length === 0) {
      // この段階には常時効果がない
      return;
    }

    // 効果にメタ情報を付与
    const effects: ActiveEffect[] = stageEffects.map((def) => ({
      ...def,
      type: def.type as ContinuousEffectType,
      uniqueId: card.uniqueId,
      cardId: card.id,
      owner,
      stage,
      usedThisTurn: false,
    }));

    this.activeEffects.set(card.uniqueId, { card, owner, effects, stage });
  }

  /**
   * フェイズカードの段階を更新（効果の再登録）
   * @param card - フェイズカード
   * @param owner - オーナー（1 or 2）
   * @param newStage - 新しい段階（0-3）
   */
  updatePhaseCardStage(card: CardWithId | MonsterInstance, owner: PlayerNumber, newStage: number): void {
    if (!card || !card.uniqueId) {
      return;
    }

    // 既存の効果を解除
    this.unregister(card.uniqueId);

    // 新しい段階で再登録
    this.registerPhaseCard(card, owner, newStage);
  }

  /**
   * 全効果をクリア（ゲームリセット時）
   */
  clear(): void {
    this.activeEffects.clear();
  }

  /**
   * ターンごとの使用フラグをリセット
   */
  resetTurnFlags(): void {
    for (const [, { effects }] of this.activeEffects) {
      effects.forEach((effect) => {
        effect.usedThisTurn = false;
      });
    }
  }

  // ========================================
  // ステータス修正計算
  // ========================================

  /**
   * 攻撃力修正値を計算
   * @param monster - 対象モンスター
   * @param context - ゲームコンテキスト
   * @returns 修正値（正: アップ, 負: ダウン）
   */
  calculateAttackModifier(monster: TargetCard, context: EffectContext): number {
    let totalModifier = 0;

    for (const [uniqueId, { owner, effects }] of this.activeEffects) {
      // コンテキストにeffectOwnerを追加
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.ATK_MODIFIER) continue;

        // ターゲットチェック
        if (!this.checkTarget(effect, monster, owner, context)) continue;

        // 条件チェック
        if (!checkCondition(effect.condition, monster, effectContext)) continue;

        // 値計算
        const effectWithId = setEffectUniqueId(effect, uniqueId);
        totalModifier += calculateValue(effectWithId, effectContext);
      }
    }

    return totalModifier;
  }

  /**
   * 攻撃力修正の詳細情報を取得
   * @param monster - 対象モンスター
   * @param context - ゲームコンテキスト
   * @returns { modifier: number, sources: Array<{name: string, value: number}> }
   */
  getAttackModifierDetails(monster: TargetCard, context: EffectContext): ModifierDetails {
    let totalModifier = 0;
    const sources: Array<{ name: string; value: number }> = [];

    for (const [uniqueId, { card, owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.ATK_MODIFIER) continue;

        if (!this.checkTarget(effect, monster, owner, context)) continue;
        if (!checkCondition(effect.condition, monster, effectContext)) continue;

        const effectWithId = setEffectUniqueId(effect, uniqueId);
        const value = calculateValue(effectWithId, effectContext);
        totalModifier += value;

        if (card && card.name && value !== 0) {
          sources.push({ name: card.name, value });
        }
      }
    }

    return { modifier: totalModifier, sources };
  }

  /**
   * HP修正値を計算
   * @param monster - 対象モンスター
   * @param context - ゲームコンテキスト
   * @returns 修正値
   */
  calculateHPModifier(monster: TargetCard, context: EffectContext): number {
    let totalModifier = 0;

    for (const [uniqueId, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.HP_MODIFIER) continue;

        if (!this.checkTarget(effect, monster, owner, context)) continue;
        if (!checkCondition(effect.condition, monster, effectContext)) continue;

        const effectWithId = setEffectUniqueId(effect, uniqueId);
        totalModifier += calculateValue(effectWithId, effectContext);
      }
    }

    return totalModifier;
  }

  /**
   * HP修正の詳細情報を取得
   * @param monster - 対象モンスター
   * @param context - ゲームコンテキスト
   * @returns { modifier: number, sources: Array<{name: string, value: number}> }
   */
  getHPModifierDetails(monster: TargetCard, context: EffectContext): ModifierDetails {
    let totalModifier = 0;
    const sources: Array<{ name: string; value: number }> = [];

    for (const [uniqueId, { card, owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.HP_MODIFIER) continue;

        if (!this.checkTarget(effect, monster, owner, context)) continue;
        if (!checkCondition(effect.condition, monster, effectContext)) continue;

        const effectWithId = setEffectUniqueId(effect, uniqueId);
        const value = calculateValue(effectWithId, effectContext);
        totalModifier += value;

        if (card && card.name && value !== 0) {
          sources.push({ name: card.name, value });
        }
      }
    }

    return { modifier: totalModifier, sources };
  }

  // ========================================
  // ダメージ関連計算
  // ========================================

  /**
   * ダメージ軽減値を計算
   * @param target - ダメージを受けるモンスター
   * @param damageSource - ダメージ源 ('battle' | 'effect')
   * @param context - ゲームコンテキスト
   * @returns 軽減値（Infinity = 完全無効）
   */
  calculateDamageReduction(target: TargetCard, damageSource: 'battle' | 'effect', context: EffectContext): number {
    let totalReduction = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        // ダメージ無効チェック
        if (effect.type === CONTINUOUS_EFFECT_TYPES.DAMAGE_IMMUNITY) {
          if (!this.checkTarget(effect, target, owner, context)) continue;
          if (!checkCondition(effect.condition, target, effectContext)) continue;

          // ダメージ源のチェック
          if (effect.damageSource && effect.damageSource !== 'all') {
            if (effect.damageSource !== damageSource) continue;
          }

          // 完全無効
          return Infinity;
        }

        // ダメージ軽減チェック
        if (effect.type === CONTINUOUS_EFFECT_TYPES.DAMAGE_REDUCTION) {
          if (!this.checkTarget(effect, target, owner, context)) continue;
          if (!checkCondition(effect.condition, target, effectContext)) continue;

          // 使用回数制限チェック
          if (effect.usesPerTurn && effect.usedThisTurn) continue;

          totalReduction += effect.value || 0;

          // 使用フラグを立てる
          if (effect.usesPerTurn) {
            effect.usedThisTurn = true;
          }
        }
      }
    }

    return totalReduction;
  }

  /**
   * 与ダメージ修正値を計算
   * @param attacker - 攻撃モンスター
   * @param context - ゲームコンテキスト
   * @param target - 攻撃対象（オプション - targetConditionチェック用）
   * @returns 修正値
   */
  calculateDamageDealtModifier(
    attacker: TargetCard,
    context: EffectContext,
    target?: TargetCard | null
  ): number {
    let totalModifier = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.DAMAGE_DEALT_MODIFIER) continue;

        // 自分のモンスターの与ダメージのみ対象
        if (attacker.owner !== owner) continue;

        if (!checkCondition(effect.condition, attacker, effectContext)) continue;

        // 対象条件がある場合、対象もチェック（例: 飛行モンスターへの攻撃時）
        if (effect.targetCondition && target) {
          if (!checkCondition(effect.targetCondition, target, effectContext)) continue;
        } else if (effect.targetCondition && !target) {
          // 対象条件があるが対象が指定されていない場合はスキップ
          continue;
        }

        totalModifier += effect.value || 0;
      }
    }

    return totalModifier;
  }

  /**
   * 被ダメージ増加値を計算（相手に対して）
   * @param target - ダメージを受けるモンスター
   * @param context - ゲームコンテキスト
   * @returns 増加値
   */
  calculateDamageReceivedModifier(target: TargetCard, context: EffectContext): number {
    let totalModifier = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.DAMAGE_RECEIVED_MODIFIER) continue;

        // 相手のモンスターのみ対象
        if (target.owner === owner) continue;

        if (!checkCondition(effect.condition, target, effectContext)) continue;

        totalModifier += effect.value || 0;
      }
    }

    return totalModifier;
  }

  // ========================================
  // コスト修正計算
  // ========================================

  /**
   * 召喚コスト修正値を計算
   * @param card - 召喚するカード
   * @param summoner - 召喚するプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns 修正値（正: 増加, 負: 軽減）
   */
  calculateSummonCostModifier(card: TargetCard, summoner: PlayerNumber, context: EffectContext): number {
    let totalModifier = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.SUMMON_COST_MODIFIER) continue;

        // ターゲットチェック
        if (effect.target === TARGET_TYPES.SELF_SUMMON && owner !== summoner) continue;
        if (effect.target === TARGET_TYPES.OPPONENT_SUMMON && owner === summoner) continue;

        // 条件チェック（カードに対して）
        if (!checkCondition(effect.condition, card, effectContext)) continue;

        totalModifier += effect.value || 0;
      }
    }

    return totalModifier;
  }

  /**
   * 召喚コスト修正の詳細情報を取得
   * @param card - 召喚するカード
   * @param summoner - 召喚するプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns { modifier: number, sources: string[] }
   */
  getSummonCostModifierDetails(card: TargetCard, summoner: PlayerNumber, context: EffectContext): CostModifierDetails {
    let totalModifier = 0;
    const sources: string[] = [];

    for (const [, { card: sourceCard, owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.SUMMON_COST_MODIFIER) continue;

        // ターゲットチェック
        if (effect.target === TARGET_TYPES.SELF_SUMMON && owner !== summoner) continue;
        if (effect.target === TARGET_TYPES.OPPONENT_SUMMON && owner === summoner) continue;

        // 条件チェック（カードに対して）
        if (!checkCondition(effect.condition, card, effectContext)) continue;

        const value = effect.value || 0;
        totalModifier += value;
        if (sourceCard && sourceCard.name) {
          sources.push(sourceCard.name);
        }
      }
    }

    return { modifier: totalModifier, sources };
  }

  /**
   * 魔法カードコスト修正値を計算
   * @param magicCard - 発動する魔法カード
   * @param caster - 発動するプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns 修正値
   */
  calculateMagicCostModifier(magicCard: TargetCard, caster: PlayerNumber, context: EffectContext): number {
    let totalModifier = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.MAGIC_COST_MODIFIER) continue;

        // ターゲットチェック
        if (effect.target === TARGET_TYPES.SELF_MAGIC && owner !== caster) {
          continue;
        }
        if (effect.target === TARGET_TYPES.OPPONENT_MAGIC && owner === caster) {
          continue;
        }

        // 条件チェック（魔法カードに対して）
        if (!checkCondition(effect.condition, magicCard, effectContext)) continue;

        totalModifier += effect.value || 0;
      }
    }

    return totalModifier;
  }

  // ========================================
  // 制限チェック
  // ========================================

  /**
   * モンスターが攻撃可能かチェック
   * @param monster - チェック対象モンスター
   * @param context - ゲームコンテキスト
   * @returns 攻撃可能ならtrue
   */
  canAttack(monster: TargetCard, context: EffectContext): boolean {
    for (const [, { owner, effects }] of this.activeEffects) {
      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.ATTACK_RESTRICTION) continue;

        // SELF_CARD の場合、そのカード自身のみ
        if (effect.target === TARGET_TYPES.SELF_CARD) {
          if (monster.uniqueId === effect.uniqueId) {
            return false;
          }
        }

        // SELF_MONSTERS の場合、オーナーの全モンスター
        if (effect.target === TARGET_TYPES.SELF_MONSTERS) {
          if (monster.owner === owner) {
            const effectContext: EffectContext = { ...context, effectOwner: owner };
            if (checkCondition(effect.condition, monster, effectContext)) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  /**
   * SP制限をチェック
   * @param player - プレイヤー（1 or 2）
   * @param _context - ゲームコンテキスト（未使用だが互換性のため残す）
   * @returns SP制限情報 または null
   */
  checkSPRestriction(player: PlayerNumber, _context: EffectContext): SPRestrictionInfo | null {
    for (const [, { owner, effects }] of this.activeEffects) {
      if (owner !== player) continue;

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.SP_RESTRICTION) continue;

        return {
          restriction: effect.restriction || 'ALL_RESTED',
          cardId: effect.cardId,
        };
      }
    }

    return null;
  }

  /**
   * SP上限を取得（変更効果適用後）
   * @param player - プレイヤー（1 or 2）
   * @param defaultLimit - デフォルトのSP上限（通常10）
   * @param _context - ゲームコンテキスト（未使用だが互換性のため残す）
   * @returns 実際のSP上限
   */
  getSPLimit(player: PlayerNumber, defaultLimit: number, _context: EffectContext): number {
    let limit = defaultLimit;

    for (const [, { owner, effects }] of this.activeEffects) {
      if (owner !== player) continue;

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.SP_LIMIT_MODIFIER) continue;

        // SP上限を効果の値に設定（最小値を採用）
        if (effect.value !== undefined && effect.value < limit) {
          limit = effect.value;
        }
      }
    }

    return limit;
  }

  // ========================================
  // 無効化チェック
  // ========================================

  /**
   * 魔法カードを無効化できるかチェック（無効化も実行）
   * @param magicCard - 発動された魔法カード
   * @param caster - 発動したプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns 無効化されたらtrue
   */
  tryNegateMagic(magicCard: TargetCard, caster: PlayerNumber, context: EffectContext): boolean {
    for (const [, { owner, effects }] of this.activeEffects) {
      // 相手の魔法のみ無効化
      if (owner === caster) continue;

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.MAGIC_NEGATION) continue;

        // 使用回数制限チェック
        if (effect.usesPerTurn && effect.usedThisTurn) continue;

        // 条件チェック
        const effectContext: EffectContext = { ...context, effectOwner: owner };
        if (!checkCondition(effect.condition, magicCard, effectContext)) continue;

        // 無効化成功
        if (effect.usesPerTurn) {
          effect.usedThisTurn = true;
        }

        return true;
      }
    }

    return false;
  }

  /**
   * 技を無効化できるかチェック（無効化も実行）
   * @param skillType - 技タイプ ('basic' | 'advanced')
   * @param skillUser - 技を使用したプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns 無効化されたらtrue
   */
  tryNegateSkill(skillType: 'basic' | 'advanced', skillUser: PlayerNumber, context: EffectContext): boolean {
    for (const [, { owner, effects }] of this.activeEffects) {
      // 相手の技のみ無効化
      if (owner === skillUser) continue;

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.SKILL_NEGATION) continue;

        // 技タイプチェック
        if (effect.skillType && effect.skillType !== 'all') {
          if (effect.skillType !== skillType) continue;
        }

        // 使用回数制限チェック
        if (effect.usesPerTurn && effect.usedThisTurn) continue;

        // 条件チェック
        const effectContext: EffectContext = { ...context, effectOwner: owner };
        if (effect.condition && !checkCondition(effect.condition, {}, effectContext)) continue;

        // 無効化成功
        if (effect.usesPerTurn) {
          effect.usedThisTurn = true;
        }

        return true;
      }
    }

    return false;
  }

  // ========================================
  // 召喚連動効果
  // ========================================

  /**
   * 召喚時バフを取得
   * @param summonedMonster - 召喚されたモンスター
   * @param summoner - 召喚したプレイヤー（1 or 2）
   * @param context - ゲームコンテキスト
   * @returns 適用されたバフ情報 { atkBuff, hpBuff }
   */
  getSummonBuffs(summonedMonster: TargetCard, summoner: PlayerNumber, context: EffectContext): SummonBuffs {
    let atkBuff = 0;
    let hpBuff = 0;

    for (const [, { owner, effects }] of this.activeEffects) {
      // 自分のモンスター召喚時のみ
      if (owner !== summoner) continue;

      const effectContext: EffectContext = { ...context, effectOwner: owner };

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.ON_SUMMON_BUFF) continue;

        // 条件チェック
        if (!checkCondition(effect.condition, summonedMonster, effectContext)) continue;

        // バフタイプに応じて加算
        if (effect.statType === 'atk' || effect.statType === 'attack') {
          atkBuff += effect.value || 0;
        } else if (effect.statType === 'hp') {
          hpBuff += effect.value || 0;
        }
      }
    }

    return { atkBuff, hpBuff };
  }

  // ========================================
  // ターゲットチェック
  // ========================================

  /**
   * ターゲットチェック
   * @param effect - 効果定義
   * @param target - チェック対象
   * @param effectOwner - 効果のオーナー
   * @param _context - ゲームコンテキスト（未使用だが互換性のため残す）
   * @returns ターゲットに該当するかどうか
   */
  private checkTarget(effect: ActiveEffect, target: TargetCard, effectOwner: PlayerNumber, _context: EffectContext): boolean {
    const targetType = effect.target;

    switch (targetType) {
      case TARGET_TYPES.SELF_CARD:
        // このカード自身
        return target.uniqueId === effect.uniqueId;

      case TARGET_TYPES.SELF_MONSTERS:
        // 自分のモンスター
        return target.owner === effectOwner;

      case TARGET_TYPES.OPPONENT_MONSTERS:
        // 相手のモンスター
        return target.owner !== effectOwner;

      case TARGET_TYPES.ALL_MONSTERS:
        // 全モンスター
        return true;

      default:
        // 未知のターゲットタイプはtrueを返す
        return true;
    }
  }

  // ========================================
  // キーワード付与チェック
  // ========================================

  /**
   * モンスターに付与されたキーワードがあるかチェック
   * @param monster - チェック対象モンスター
   * @param keyword - チェックするキーワード（例: '対空'）
   * @param context - ゲームコンテキスト
   * @returns キーワードが付与されていればtrue
   */
  hasGrantedKeyword(monster: TargetCard, keyword: string, context: EffectContext): boolean {
    for (const [, { owner, effects }] of this.activeEffects) {
      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.GRANT_KEYWORD) continue;

        // キーワードチェック
        if ((effect as unknown as { keyword: string }).keyword !== keyword) continue;

        // ターゲットチェック（自分のモンスターのみ）
        if (!this.checkTarget(effect, monster, owner, context)) continue;

        // 条件チェック
        const effectContext: EffectContext = { ...context, effectOwner: owner };
        if (!checkCondition(effect.condition, monster, effectContext)) continue;

        return true;
      }
    }

    return false;
  }

  // ========================================
  // ダイレクトダメージ軽減（飛行用）
  // ========================================

  /**
   * 飛行モンスターからのダイレクトダメージ軽減率を取得
   * @param defender - 防御プレイヤー（1 or 2）
   * @param attackerHasFlight - 攻撃者が飛行を持つか
   * @param context - ゲームコンテキスト
   * @returns ダメージ倍率（1.0 = 軽減なし, 0.75 = 25%軽減）
   */
  getDirectDamageReductionMultiplier(
    defender: PlayerNumber,
    attackerHasFlight: boolean,
    context: EffectContext
  ): number {
    let multiplier = 1.0;

    for (const [, { owner, effects }] of this.activeEffects) {
      // 防御プレイヤーのフィールドカード効果のみ
      if (owner !== defender) continue;

      for (const effect of effects) {
        if (effect.type !== CONTINUOUS_EFFECT_TYPES.DIRECT_DAMAGE_REDUCTION) continue;

        // 飛行条件チェック
        const sourceCondition = (effect as unknown as { sourceCondition?: { hasFlight?: boolean } }).sourceCondition;
        if (sourceCondition?.hasFlight && !attackerHasFlight) continue;

        // 軽減率を適用（乗算）
        const reductionRate = effect.value || 0;
        multiplier *= (1 - reductionRate);
      }
    }

    return multiplier;
  }

  // ========================================
  // デバッグ・ユーティリティ
  // ========================================

  /**
   * 登録されている全効果を取得（デバッグ用）
   * @returns アクティブな効果のMap
   */
  getActiveEffects(): Map<string, EffectEntry> {
    return this.activeEffects;
  }

  /**
   * 特定カードの効果を取得
   * @param uniqueId - カードのuniqueId
   * @returns 効果配列
   */
  getEffectsForCard(uniqueId: string): ActiveEffect[] {
    const entry = this.activeEffects.get(uniqueId);
    return entry ? entry.effects : [];
  }

  /**
   * 登録されているカード数を取得
   * @returns 登録数
   */
  getRegisteredCount(): number {
    return this.activeEffects.size;
  }

  /**
   * デバッグ情報をログ出力
   */
  debugLog(): void {
    console.log('=== Continuous Effect Engine Debug ===');
    console.log(`Registered cards: ${this.activeEffects.size}`);
    for (const [, { card, owner, effects }] of this.activeEffects) {
      console.log(`  ${card.name} (${card.id}) - Owner: P${owner}`);
      effects.forEach((e, i) => {
        console.log(`    [${i}] ${e.type} - usedThisTurn: ${e.usedThisTurn}`);
      });
    }
  }
}

// シングルトンインスタンスをエクスポート
export const continuousEffectEngine = new ContinuousEffectEngine();

// クラス自体もエクスポート（テスト用）
export { ContinuousEffectEngine };
